import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from './supabase'

export interface OpenOrderInfo {
  id: string
  order_number: string
  tracking_token: string | null
  tracking_link: string | null
  status: string
  created_at: string
  total: number
}

const DISMISSED_KEY_PREFIX = 'noiza_open_order_dismissed:'

/**
 * Hook that checks if the current customer (by saved phone) has an open order
 * for the given restaurant. "Open" = not delivered, not cancelled.
 *
 * Returns the open order info + helpers to show/dismiss the alert.
 * The check runs once on mount and caches the result for the session.
 */
export function useOpenOrderCheck(
  restaurantId: string | number | null | undefined,
) {
  const [openOrder, setOpenOrder] = useState<OpenOrderInfo | null>(null)
  const [loading, setLoading] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const [checked, setChecked] = useState(false)
  const checkRanRef = useRef(false)

  const rid = restaurantId ? String(restaurantId) : null
  const dismissedKey = rid ? `${DISMISSED_KEY_PREFIX}${rid}` : null

  // Check if already dismissed this session
  useEffect(() => {
    if (dismissedKey && sessionStorage.getItem(dismissedKey) === 'true') {
      setDismissed(true)
    }
  }, [dismissedKey])

  // Run the check once
  useEffect(() => {
    if (!rid || checkRanRef.current) return

    const savedPhone = localStorage.getItem('noiza_customer_phone')
    if (!savedPhone) {
      setChecked(true)
      return
    }

    checkRanRef.current = true
    setLoading(true)

    const doCheck = async () => {
      try {
        console.log('[OpenOrderCheck] Checking for open orders:', {
          restaurantId: rid,
          phone: savedPhone,
        })

        const { data, error } = await supabase
          .from('orders')
          .select('id, order_number, tracking_token, status, created_at, total')
          .eq('restaurant_id', rid)
          .eq('customer_phone', savedPhone)
          .not('status', 'in', '("delivered","cancelled")')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle()

        if (error) {
          console.error('[OpenOrderCheck] Query error:', error)
          return
        }

        if (data) {
          const trackingLink = data.tracking_token
            ? `${window.location.origin}/track/${data.tracking_token}`
            : null

          console.log('[OpenOrderCheck] Open order found:', data.order_number)
          setOpenOrder({
            id: data.id,
            order_number: data.order_number,
            tracking_token: data.tracking_token,
            tracking_link: trackingLink,
            status: data.status,
            created_at: data.created_at,
            total: data.total,
          })
        } else {
          console.log('[OpenOrderCheck] No open orders found')
        }
      } catch (err) {
        console.error('[OpenOrderCheck] Exception:', err)
      } finally {
        setLoading(false)
        setChecked(true)
      }
    }

    doCheck()
  }, [rid])

  const dismiss = useCallback(() => {
    setDismissed(true)
    if (dismissedKey) {
      sessionStorage.setItem(dismissedKey, 'true')
    }
    console.log('[OpenOrderCheck] Alert dismissed for restaurant:', rid)
  }, [dismissedKey, rid])

  return {
    /** The open order info, or null if none found */
    openOrder,
    /** Whether the check is still loading */
    loading,
    /** Whether the user has dismissed the alert this session */
    dismissed,
    /** Whether the initial check has completed */
    checked,
    /** Whether we should show the alert (has open order + not dismissed + check done) */
    shouldAlert: !!openOrder && !dismissed && checked,
    /** Dismiss the alert for this session */
    dismiss,
  }
}
